import React, { ReactElement, useRef, useState } from 'react'
import { Column as BaseColumn } from '../../types'

import * as S from './styled'
import Scrollbar from 'react-scrollbars-custom'
import { ScrollState } from 'react-scrollbars-custom/dist/types/types'
import { useMemo } from 'react'
import { contains } from '../../utils/index'

export interface Column<T = any> extends Omit<BaseColumn<T>, 'renderItem'> {
    fixed?: boolean
    renderItem?: (value: any, item: T, column: Column<T>) => React.ReactElement | string
}

export interface Footer<T = any> {
    key: string | number
    dataIndex: keyof T | string
    fixed?: boolean
    footerColSpan?: number
    renderFooter?: (data: any[], footer: Footer<T>) => React.ReactElement | string
}
interface Props<T = any> {
    filterProps?: {
        term: string
        column: string
    }
    columns: Column<T>[]
    footer?: Footer<T>[]
    data: any[]
    selectable?: boolean
    onClickRow?: (row: T, rowIndex: number) => void
}

const FixedColumnTable = <T extends any>({
    columns,
    footer,
    data,
    onClickRow,
    filterProps
}: Props<T>): ReactElement<Props<T>> => {
    const [selectedIndex, setSelectedIndex] = useState<number>()
    const scrollRef = useRef<any>(null)

    const [fixedColumns, commonColumns] = useMemo(() => {
        let fixed: Column<T>[] = []
        let common: Column<T>[] = []

        for (const column of columns) {
            if (column.fixed) {
                fixed.push(column)
            } else {
                common.push(column)
            }
        }

        return [fixed, common]
    }, [columns])

    const [fixedFooter, commonFooter] = useMemo(() => {
        let fixed: Footer<T>[] = []
        let common: Footer<T>[] = []

        if (footer) {
            for (const f of footer) {
                if (f.fixed) {
                    fixed.push(f)
                } else {
                    common.push(f)
                }
            }
        }

        return [fixed, common]
    }, [footer])

    const renderHeader = (column: Column<T>) => {
        const { key, width, renderColumn, title, fixed } = column
        return (
            <S.Th key={key} width={width} className={!fixed ? 'sheet' : undefined}>
                {renderColumn ? renderColumn(column as any) : title}
            </S.Th>
        )
    }

    const renderFooter = (footer: Footer<T>) => {
        const { key, renderFooter, fixed, footerColSpan } = footer
        return (
            <S.Tf
                key={key}
                className={!fixed ? 'sheet' : undefined}
                colSpan={footerColSpan}
            >
                {renderFooter && renderFooter(filteredData, footer)}
            </S.Tf>
        )
    }

    const onScroll = (scrollTop: number) => {
        scrollRef.current.scrollTo(0, scrollTop)
    }

    const renderRow = (row: T, header: Column<T>[], rowIndex: number) => {
        return (
            <S.Tr
                key={rowIndex}
                className={rowIndex === selectedIndex ? 'selected' : onClickRow ? 'selectable' : undefined}
                onClick={() => {
                    if (onClickRow) {
                        onClickRow(row, rowIndex)
                        setSelectedIndex(rowIndex)
                    }
                }}
            >
                {header.map((column, cellIndex: number) => {
                    const { dataIndex, renderItem, width, fixed } = column
                    const key = `${rowIndex}-${cellIndex}`
                    const value = row[dataIndex as keyof T]
                    return (
                        <S.Td key={key} width={width} className={!fixed ? 'sheet' : undefined}>
                            <div>{renderItem ? renderItem(value, row, column) : value}</div>
                        </S.Td>
                    )
                })}
            </S.Tr>
        )
    }

    const [fixedRows, commonRows, filteredData] = useMemo(() => {
        let index = 0
        let filteredData: typeof data = []
        let fixedRows: JSX.Element[] = []
        let commonRows: JSX.Element[] = []
        for (const row of data) {
            if (!filterProps || !filterProps.term || contains(row[filterProps.column], filterProps.term)) {
                fixedRows.push(renderRow(row, fixedColumns, index))
                commonRows.push(renderRow(row, commonColumns, index))
                filteredData.push(row)
                index++
            }
        }
        return [fixedRows, commonRows, filteredData]
    }, [data, fixedColumns, commonColumns, filterProps])

    return (
        <S.Container length={fixedRows.length}>
            <Scrollbar
                ref={scrollRef}
                noScroll
                style={{
                    minWidth: 'calc(100% - 350px)',
                    width: 'calc(100% - 350px)',
                    marginRight: -1
                }}
            >
                <S.Table>
                    <S.Thead>
                        <S.Tr>{fixedColumns.map(column => renderHeader(column))}</S.Tr>
                    </S.Thead>
                    <S.Tbody>{fixedRows}</S.Tbody>
                    <S.Tfoot>
                        <S.Tr>{fixedFooter.map(footer => renderFooter(footer))}</S.Tr>
                    </S.Tfoot>
                </S.Table>
            </Scrollbar>

            <Scrollbar
                onScroll={
                    (({ scrollTop }: ScrollState) => {
                        onScroll(scrollTop)
                    }) as any
                }
            >
                <S.Table>
                    <S.Thead>
                        <S.Tr>{commonColumns.map(column => renderHeader(column))}</S.Tr>
                    </S.Thead>
                    <S.Tbody>{commonRows}</S.Tbody>
                    <S.Tfoot>
                        <S.Tr>{commonFooter.map(footer => renderFooter(footer))}</S.Tr>
                    </S.Tfoot>
                </S.Table>
            </Scrollbar>
        </S.Container>
    )
}

export default React.memo(FixedColumnTable)
